import { useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Check, X } from 'lucide-react';
import { useCVData } from '../../lib/cv-mock-data';
import {
  getSuggestedFamiliesFromProfile,
  searchCanonicalSkills,
} from '../../lib/job-taxonomy';
import { WebSaveButton, WebFormDialog } from '../cv/forms/web-form-elements';
import {
  useHydratedArrayState,
  useHydratedValueState,
} from '../cv/forms/useHydratedFormState';
import { confirmDeleteCvItem } from '../cv/forms/delete-confirmation';

interface Props {
  onSave?: () => Promise<void> | void;
  ctaLabel?: string;
}

const BRAND = '#5a2aff';

function dedupe(values: string[]) {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    const lower = trimmed.toLowerCase();
    if (!trimmed || seen.has(lower)) continue;
    seen.add(lower);
    out.push(trimmed);
  }
  return out;
}

function isEmptyAttributionMap(value: Record<string, string[]>): boolean {
  return Object.keys(value).length === 0;
}

/**
 * Web variant of the redesigned onboarding skills step. See the native
 * SkillsAttributionForm for the full behaviour notes: the two share data flow
 * (selected skills → skillGroups; per-item attributions → local CV draft only).
 */
export function SkillsAttributionForm({ onSave, ctaLabel }: Props) {
  const { t, i18n } = useTranslation('auth');
  const { t: tSeeker } = useTranslation('seeker');
  const { profile, updateProfile } = useCVData();
  const isAr = (i18n.language || '').toLowerCase().startsWith('ar');

  const [isSaving, setIsSaving] = useState(false);
  const [search, setSearch] = useState('');
  const profileSelectedSkills = useMemo(
    () => dedupe(profile.skillGroups.flatMap((group) => group.items)),
    [profile.skillGroups],
  );
  const profileSkillAttributions = useMemo(
    () => ({ ...(profile.skillAttributions ?? {}) }),
    [profile.skillAttributions],
  );
  const [selected, setSelected] = useHydratedArrayState(profileSelectedSkills);
  const [attributions, setAttributions] = useHydratedValueState(
    profileSkillAttributions,
    isEmptyAttributionMap,
  );
  const [activeSkill, setActiveSkill] = useState<string | null>(null);

  const suggestedFamilies = useMemo(
    () => getSuggestedFamiliesFromProfile(profile, i18n.language),
    [i18n.language, profile],
  );

  const skillFamilyLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of searchCanonicalSkills('', 1000)) {
      map.set(option.label.toLowerCase(), isAr ? option.familyLabelAr : option.familyLabel);
    }
    return map;
  }, [isAr]);

  const baseSuggestions = useMemo(
    () => dedupe(suggestedFamilies.flatMap((family) => family.skills)),
    [suggestedFamilies],
  );

  const gridSkills = useMemo(() => {
    const query = search.trim();
    const pool = query
      ? searchCanonicalSkills(query, 40).map((option) => option.label)
      : baseSuggestions;
    const selectedLower = new Set(selected.map((s) => s.toLowerCase()));
    return dedupe(pool).filter((skill) => !selectedLower.has(skill.toLowerCase()));
  }, [search, baseSuggestions, selected]);

  const customAddable = useMemo(() => {
    const query = search.trim();
    if (!query) return null;
    const lower = query.toLowerCase();
    const known = new Set([
      ...selected.map((s) => s.toLowerCase()),
      ...gridSkills.map((s) => s.toLowerCase()),
    ]);
    return known.has(lower) ? null : query;
  }, [search, selected, gridSkills]);

  const sections = useMemo(() => {
    const experience = profile.experience.map((item) => ({
      id: item.id,
      label: item.company
        ? t('onboarding.skills.roleAtCompany', {
            role: item.role,
            company: item.company,
            defaultValue: `${item.role} at ${item.company}`,
          })
        : item.role,
    }));
    const education = profile.education.map((item) => ({
      id: item.id,
      label: item.school || item.degree,
    }));
    const certifications = profile.certifications.map((item) => ({
      id: item.id,
      label: item.name,
    }));
    return [
      { key: 'experience', title: t('onboarding.skills.sectionExperience'), items: experience },
      { key: 'education', title: t('onboarding.skills.sectionEducation'), items: education },
      {
        key: 'certifications',
        title: t('onboarding.skills.sectionCertifications'),
        items: certifications,
      },
    ].filter((section) => section.items.some((item) => item.label.trim()));
  }, [profile.experience, profile.education, profile.certifications, t]);

  const selectSkill = (skill: string) => {
    const value = skill.trim();
    if (!value) return;
    setSelected((current) => dedupe([...current, value]));
    setSearch('');
    // Open the attribution modal only when there is something to link to;
    // otherwise just add the chip so several skills can be added in a row.
    if (sections.length > 0) {
      setActiveSkill(value);
    }
  };

  // Re-open the attribution modal for an already-selected chip.
  const openAttribution = (skill: string) => {
    if (sections.length === 0) return;
    setSearch('');
    setActiveSkill(skill);
  };

  const removeSkillImmediately = (skill: string) => {
    setSelected((current) => current.filter((item) => item !== skill));
    setAttributions((current) => {
      if (!(skill in current)) return current;
      const next = { ...current };
      delete next[skill];
      return next;
    });
    setActiveSkill((current) => (current === skill ? null : current));
  };

  const removeSkill = (skill: string) => {
    void confirmDeleteCvItem(tSeeker, 'skill', () => removeSkillImmediately(skill));
  };

  const toggleAttribution = (skill: string, itemId: string) => {
    setAttributions((current) => {
      const existing = current[skill] ?? [];
      const next = existing.includes(itemId)
        ? existing.filter((id) => id !== itemId)
        : [...existing, itemId];
      return { ...current, [skill]: next };
    });
  };

  const buildSkillGroups = () => {
    const selectedLower = new Set(selected.map((s) => s.toLowerCase()));
    const covered = new Set<string>();
    const groups: { category: string; items: string[] }[] = [];

    for (const group of profile.skillGroups) {
      const items = group.items.filter((item) => selectedLower.has(item.toLowerCase()));
      items.forEach((item) => covered.add(item.toLowerCase()));
      if (items.length) groups.push({ category: group.category, items });
    }

    const remaining = selected.filter((skill) => !covered.has(skill.toLowerCase()));
    for (const skill of remaining) {
      const category =
        skillFamilyLabel.get(skill.toLowerCase()) || t('onboarding.skills.otherCategory');
      const existing = groups.find((group) => group.category === category);
      if (existing) existing.items = dedupe([...existing.items, skill]);
      else groups.push({ category, items: [skill] });
    }

    return groups;
  };

  const handleSave = async () => {
    if (isSaving) return;
    const selectedSet = new Set(selected);
    const prunedAttributions = Object.fromEntries(
      Object.entries(attributions).filter(
        ([skill, ids]) => selectedSet.has(skill) && ids.length > 0,
      ),
    );
    updateProfile({
      skillGroups: buildSkillGroups(),
      skillAttributions: prunedAttributions,
    });
    setIsSaving(true);
    try {
      await onSave?.();
    } catch {
      // The onboarding provider surfaces save failures with its own toast.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Search */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          height: 50,
          paddingInline: 18,
          borderRadius: 9999,
          backgroundColor: 'rgba(0,0,0,0.04)',
          border: '1px solid var(--input-border)',
        }}
      >
        <Search size={20} color="var(--text-tertiary)" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && customAddable) selectSkill(customAddable);
          }}
          placeholder={t('onboarding.skills.searchPlaceholder')}
          style={{
            flex: 1,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
            fontSize: 14,
          }}
        />
      </div>

      {/* Selected skill chips */}
      {selected.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {selected.map((skill) => {
            // A persistent check marks skills linked to at least one
            // experience / education / certification (the modal's whole point).
            const linked = (attributions[skill]?.length ?? 0) > 0;
            return (
              <span
                key={skill}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  paddingInline: 14,
                  paddingBlock: 7,
                  borderRadius: 9999,
                  backgroundColor: BRAND,
                  color: '#fff',
                  border: linked ? '2px solid rgba(255,255,255,0.55)' : '2px solid transparent',
                }}
              >
                {linked ? <Check size={14} strokeWidth={3} /> : null}
                <button
                  type="button"
                  onClick={() => openAttribution(skill)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'inherit',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: 'pointer',
                    padding: 0,
                    textTransform: 'capitalize',
                  }}
                >
                  {skill}
                </button>
                <button
                  type="button"
                  onClick={() => removeSkill(skill)}
                  aria-label={`Remove ${skill}`}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.85)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    padding: 0,
                  }}
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      {/* Suggestion grid: always visible so multiple skills add in a row. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {customAddable ? (
          <button
            type="button"
            onClick={() => selectSkill(customAddable)}
            style={gridChipStyle}
          >
            <span style={{ textTransform: 'none' }}>{`${t('onboarding.skills.add')} “${customAddable}”`}</span>
            <Plus size={14} color={BRAND} strokeWidth={2.5} />
          </button>
        ) : null}
        {gridSkills.map((skill) => (
          <button key={skill} type="button" onClick={() => selectSkill(skill)} style={gridChipStyle}>
            <span>{skill}</span>
            <Plus size={14} color="var(--text-tertiary)" strokeWidth={2.25} />
          </button>
        ))}
      </div>

      <WebSaveButton
        label={ctaLabel ?? t('onboarding.next', { defaultValue: 'Next' })}
        onClick={handleSave}
        loading={isSaving}
        disabled={isSaving}
      />

      {/* Attribution modal: link the active skill to the user's experience /
          education / certifications, so several skills can be added in a row. */}
      <WebFormDialog
        visible={activeSkill !== null}
        title={t('onboarding.skills.linkTitle', { defaultValue: 'Where did you use this?' })}
        onClose={() => setActiveSkill(null)}
        footer={
          <button
            type="button"
            onClick={() => setActiveSkill(null)}
            style={{
              width: '100%',
              height: 48,
              borderRadius: 9999,
              backgroundColor: 'var(--text-primary)',
              color: 'var(--bg)',
              fontSize: 16,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {t('onboarding.skills.done', { defaultValue: 'Done' })}
          </button>
        }
      >
        {activeSkill ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: BRAND, textTransform: 'capitalize' }}>
              {activeSkill}
            </span>
            {sections.map((section) => (
              <div key={section.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <span
                  style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    marginBottom: 4,
                    textTransform: 'capitalize',
                  }}
                >
                  {section.title}
                </span>
                {section.items.map((item) => {
                  const checked = (attributions[activeSkill] ?? []).includes(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => toggleAttribution(activeSkill, item.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        paddingBlock: 6,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        textAlign: 'start',
                        fontFamily: 'inherit',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: 8,
                          flexShrink: 0,
                          border: `1.5px solid ${checked ? BRAND : 'var(--input-border)'}`,
                          backgroundColor: checked ? BRAND : 'transparent',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {checked ? <Check size={16} strokeWidth={3} /> : null}
                      </span>
                      <span style={{ fontSize: 14, textTransform: 'capitalize' }}>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        ) : null}
      </WebFormDialog>
    </div>
  );
}

const gridChipStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  paddingBlock: 8,
  paddingInline: 14,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  backgroundColor: 'var(--tabbio-button-soft-bg)',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  textTransform: 'capitalize',
};
